/**
 * Провайдер состояния авторизации и поток входа (auth-and-roles / client-cabinet-shell).
 *
 * Старт приложения: `GET /api/setup/status` → если нужна первичная настройка, ведём на /setup;
 * иначе `GET /api/me/context` → 401 → /login, успех → кабинет. Состояние пользователя и навигации
 * раздаётся через React-контекст.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  ApiError,
  authApi,
  onOrganizationEvent,
  resetCsrfCache,
  setActiveOrganization,
  type LoginInput,
  type MeContext,
  type RegisterIndividualInput,
  type SetupAdminInput,
} from '@/lib/api'
import { reloadApp } from '@/lib/hard-navigate'

export type AuthStatus = 'loading' | 'needs-setup' | 'unauthenticated' | 'authenticated'

export interface AuthContextValue {
  status: AuthStatus
  /** Контекст пользователя/навигации; задан только при status === 'authenticated'. */
  context: MeContext | null
  /**
   * Клиент-компания вошёл, но организация не выбрана (за входом несколько организаций) или её
   * открепили: кабинета ещё нет — показывается окно выбора организации (multi-organization-login).
   */
  needsOrganization: boolean
  login: (input: LoginInput) => Promise<void>
  logout: () => Promise<void>
  setupAdmin: (input: SetupAdminInput) => Promise<void>
  register: (input: RegisterIndividualInput) => Promise<void>
  /** Перечитать контекст (например, после отметки уведомлений прочитанными). */
  refresh: () => Promise<void>
  /**
   * Выбрать активную организацию (окно выбора, переключатель в шапке) и перезагрузить кабинет:
   * так на экране не остаётся данных прежней организации.
   */
  selectOrganization: (clientId: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthState {
  status: AuthStatus
  context: MeContext | null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', context: null })

  // Контекст пользователя → состояние. Организацию вкладки запоминаем СРАЗУ, до отрисовки
  // страниц: с ней уходит заголовок X-Org-Id каждого запроса (защита от «чужой» вкладки).
  const applyContext = useCallback((context: MeContext) => {
    setActiveOrganization(context.user.org?.id ?? null)
    setState({ status: 'authenticated', context })
  }, [])

  const loadContext = useCallback(async () => {
    applyContext(await authApi.context())
  }, [applyContext])

  // Загрузка состояния при старте приложения.
  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      try {
        const { needsSetup } = await authApi.setupStatus()
        if (cancelled) return
        if (needsSetup) {
          setState({ status: 'needs-setup', context: null })
          return
        }
        const context = await authApi.context()
        if (!cancelled) applyContext(context)
      } catch (err) {
        if (cancelled) return
        // 401 — обычная «нет сессии»; прочее (сеть/бэкенд) тоже ведём на вход.
        if (!(err instanceof ApiError) || err.status !== 401) {
          // Неожиданная ошибка старта (сеть/бэкенд) — не валим приложение, ведём на вход.
          console.error('auth bootstrap failed', err)
        }
        setState({ status: 'unauthenticated', context: null })
      }
    }
    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [applyContext])

  const login = useCallback(
    async (input: LoginInput) => {
      await authApi.login(input)
      await loadContext()
    },
    [loadContext],
  )

  const setupAdmin = useCallback(
    async (input: SetupAdminInput) => {
      await authApi.setupAdmin(input)
      await loadContext()
    },
    [loadContext],
  )

  const register = useCallback(
    async (input: RegisterIndividualInput) => {
      await authApi.register(input)
      await loadContext()
    },
    [loadContext],
  )

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      resetCsrfCache()
      setActiveOrganization(null)
      setState({ status: 'unauthenticated', context: null })
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      applyContext(await authApi.context())
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setActiveOrganization(null)
        setState({ status: 'unauthenticated', context: null })
      }
    }
  }, [applyContext])

  const selectOrganization = useCallback(async (clientId: string) => {
    await authApi.selectOrganization(clientId)
    // Данные прежней организации с экрана убираем перезагрузкой кабинета целиком.
    reloadApp('/')
  }, [])

  // 409 по организации от любого запроса: организацию открепили — перечитываем контекст (откроется
  // окно выбора); в другой вкладке переключили организацию — эта вкладка перезагружается.
  useEffect(
    () =>
      onOrganizationEvent((event) => {
        if (event === 'organization_changed') reloadApp(window.location.pathname + window.location.search)
        else void refresh()
      }),
    [refresh],
  )

  const needsOrganization =
    state.status === 'authenticated' &&
    state.context?.user.role === 'company' &&
    !state.context.user.org

  const value = useMemo<AuthContextValue>(
    () => ({
      status: state.status,
      context: state.context,
      needsOrganization,
      login,
      logout,
      setupAdmin,
      register,
      refresh,
      selectOrganization,
    }),
    [
      state.status,
      state.context,
      needsOrganization,
      login,
      logout,
      setupAdmin,
      register,
      refresh,
      selectOrganization,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

/**
 * Контекст кабинета, если он есть (иначе null) — для общих хуков, которым роль нужна лишь для
 * формулировок и работают и вне AuthProvider (тесты отдельных страниц).
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext)
}
